const WISHLIST_STORAGE_KEY = "theme:wishlist:v1";
const WL_DEBUG = false;
/** @param {...any} args */
const dbg = (...args) => {
    if (WL_DEBUG) console.debug("[wishlist]", ...args);
};

/** @typedef {{ productHandle: string, productId?: number, variantId?: number|string|null, addedAt: number }} WishlistItem */
/** @typedef {{ productHandle: string, productId?: number, variantId?: number|string|null }} WishlistAddItem */

const WishlistStore = {
    /** @param {WishlistItem[]} items */
    _normalize(items) {
        const map = new Map();
        for (const it of Array.isArray(items) ? items : []) {
            const key =
                it.variantId != null && it.variantId !== "" ? `v-${String(it.variantId)}` : `p-${it.productHandle}`;
            const prev = map.get(key);
            if (!prev || (it.addedAt ?? 0) > (prev.addedAt ?? 0)) map.set(key, it);
        }
        return Array.from(map.values());
    },
    /** @returns {WishlistItem[]} */
    get() {
        try {
            const raw = localStorage.getItem(WISHLIST_STORAGE_KEY);
            const parsed = raw ? JSON.parse(raw) : [];
            if (Array.isArray(parsed)) return this._normalize(parsed);
        } catch (_) {}
        return [];
    },
    /** @param {WishlistItem[]} items */
    set(items) {
        const normalized = this._normalize(items);
        localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(normalized));
        dbg("store:set", { count: normalized.length });
        document.dispatchEvent(
            new CustomEvent("wishlist:change", {
                detail: { count: normalized.length, items: normalized },
            }),
        );
    },
    /** @param {WishlistAddItem} item */
    add(item) {
        const items = this.get();
        // De-dupe by variantId if present, else by productHandle
        const exists = items.some((i) =>
            item.variantId ? String(i.variantId) === String(item.variantId) : i.productHandle === item.productHandle,
        );
        if (!exists) {
            items.push({ ...item, addedAt: Date.now() });
            this.set(items);
        }
    },
    /** @param {{productHandle: string, variantId?: string|number|null}} key */
    remove(key) {
        const items = this.get().filter((i) => {
            if (key.variantId != null && key.variantId !== "") {
                return String(i.variantId) !== String(key.variantId);
            }
            return i.productHandle !== key.productHandle;
        });
        this.set(items);
    },
    /** @param {{productHandle: string, variantId?: string|number|null}} key */
    has(key) {
        return this.get().some((i) =>
            key.variantId != null && key.variantId !== ""
                ? String(i.variantId) === String(key.variantId)
                : i.productHandle === key.productHandle,
        );
    },
    /** @returns {number} */
    count() {
        return this.get().length;
    },
};

// Expose WishlistStore globally for use by other components (like product-quick-view)
window.WishlistStore = WishlistStore;

// Header icon component updates
function updateWishlistIconCounts(animate = true) {
    const el = document.querySelector("wishlist-icon");
    const drawer = document.querySelector("wishlist-drawer-component");
    const bubble = el?.querySelector?.('[ref="wishlistBubble"]');
    const text = el?.querySelector?.('[ref="wishlistBubbleText"]');
    const countNode = el?.querySelector?.('[ref="wishlistBubbleCount"]');
    const drawerBubble = drawer?.querySelector?.('[ref="wishlistDrawerBubble"]');
    const drawerText = drawer?.querySelector?.('[ref="wishlistDrawerBubbleText"]');
    const drawerCountNode = drawer?.querySelector?.('[ref="wishlistDrawerBubbleCount"]');

    const count = WishlistStore.count();
    try {
        sessionStorage.setItem("wishlist-count", JSON.stringify({ value: String(count), timestamp: Date.now() }));
    } catch (_) {}
    const display = count > 0 && count < 100 ? String(count) : count >= 100 ? "" : "0";
    if (countNode && bubble && el) {
        countNode.textContent = display;
        bubble.classList.toggle("visually-hidden", count === 0);
        countNode.classList.toggle("hidden", count === 0);
        el.classList.toggle("header-actions__wishlist-icon--has-items", count > 0);
        if (animate && count > 0 && text) {
            bubble.classList.add("wishlist-bubble--animating");
            text.addEventListener("animationend", () => bubble.classList.remove("wishlist-bubble--animating"), {
                once: true,
            });
        }
    }
    if (drawerCountNode && drawerBubble) {
        drawerCountNode.textContent = display;
        drawerBubble.classList.toggle("visually-hidden", count === 0);
        drawerCountNode.classList.toggle("hidden", count === 0);
    }
}

function ensureWishlistBubbleVisible() {
    const el = document.querySelector("wishlist-icon");
    if (!el) return;
    const bubble = el.querySelector('[ref="wishlistBubble"]');
    const text = el.querySelector('[ref="wishlistBubbleText"]');
    const countNode = el.querySelector('[ref="wishlistBubbleCount"]');
    if (!(bubble && text && countNode)) return;
    const count = WishlistStore.count();
    if (count > 0) {
        countNode.textContent = count < 100 ? String(count) : "";
        bubble.classList.remove("visually-hidden");
        countNode.classList.remove("hidden");
        el.classList.add("header-actions__wishlist-icon--has-items");
    }
}

function reassertWishlistBubbleWithRetries() {
    // Try multiple times to defeat late DOM mutations/renders
    const delays = [0, 16, 48, 96, 160];
    for (const d of delays) setTimeout(ensureWishlistBubbleVisible, d);
    requestAnimationFrame(ensureWishlistBubbleVisible);
}

// Ensure the bubble updates whenever the header icon is (re)attached
(() => {
    try {
        if (!customElements.get("wishlist-icon")) {
            class WishlistIconElement extends HTMLElement {
                connectedCallback() {
                    // Defer to allow any inner markup to settle
                    requestAnimationFrame(() => {
                        // Use sessionStorage hydration first to avoid a visible flicker
                        try {
                            const raw = sessionStorage.getItem("wishlist-count");
                            if (raw) {
                                const { value, timestamp } = JSON.parse(raw);
                                if (Date.now() - timestamp < 10000 && Number(value) > 0) {
                                    ensureWishlistBubbleVisible();
                                }
                            }
                        } catch (_) {}
                        updateWishlistIconCounts(false);
                        reassertWishlistBubbleWithRetries();
                    });
                }
            }
            customElements.define("wishlist-icon", WishlistIconElement);
        }
    } catch (_) {}
})();

// Try to initialize the bubble ASAP (in case DOM is already parsed)
try {
    updateWishlistIconCounts(false);
} catch (_) {}
// Keep bubble correct across navigation and visibility changes
window.addEventListener("pageshow", () => updateWishlistIconCounts(false));
document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") updateWishlistIconCounts(false);
});
// Re-apply after Shopify sections or DOM morphing potentially replace the header
document.addEventListener("shopify:section:load", () => updateWishlistIconCounts(false));
document.addEventListener("shopify:section:select", () => updateWishlistIconCounts(false));
// Observe DOM changes to catch header/icon re-render
(() => {
    let scheduled = false;
    const schedule = () => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
            scheduled = false;
            updateWishlistIconCounts(false);
        });
    };
    try {
        const mo = new MutationObserver((mutationList) => {
            for (const m of mutationList) {
                if (m.type === "childList") {
                    // If a wishlist icon was added or modified, refresh
                    if (
                        Array.from(m.addedNodes).some(
                            (n) =>
                                n instanceof Element &&
                                (n.matches?.("wishlist-icon") || n.querySelector?.("wishlist-icon")),
                        )
                    ) {
                        schedule();
                        return;
                    }
                }
            }
        });
        mo.observe(document.documentElement || document.body, { childList: true, subtree: true });
    } catch (_) {}
})();

// Find nearest product context
/** @param {HTMLElement} btn */
function getContextForButton(btn) {
    const productCard = /** @type {any} */ (btn.closest("product-card"));
    const productId = btn.getAttribute("data-product-id");
    const productHandle = btn.getAttribute("data-product-handle");
    let variantId = btn.getAttribute("data-variant-id") || null;
    // If inside a product-card and no variant on the button, try to resolve current selected variant
    if (!variantId && productCard && typeof productCard.getSelectedVariantId === "function") {
        const selected = productCard.getSelectedVariantId();
        if (selected) variantId = selected;
    }
    return { productId, productHandle, variantId };
}

/** @param {HTMLElement} btn @param {boolean} active */
function toggleButtonState(btn, active) {
    if (!(btn instanceof HTMLElement)) return;
    btn.setAttribute("aria-pressed", active ? "true" : "false");
    btn.classList.toggle("wishlist-button--active", !!active);
}

function syncAllButtons() {
    document.querySelectorAll("[data-wishlist-button]").forEach((el) => {
        const btn = /** @type {HTMLElement} */ (el);
        const { productHandle, variantId } = getContextForButton(btn);
        if (!productHandle) return;
        const active = WishlistStore.has({ productHandle, variantId });
        toggleButtonState(btn, active);
    });
}

// Handle clicks on wishlist (heart) buttons: open Product Quick View popup
document.addEventListener("click", (e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;
    const btn = /** @type {HTMLElement | null} */ (target.closest("[data-wishlist-button]"));
    if (!btn) return;
    // Always open the Product Quick View (solid black ATC) instead of Quick Add / wishlist toggle
    e.preventDefault();
    e.stopPropagation();
    try {
        const { productHandle } = getContextForButton(btn);
        if (productHandle && window.productQuickView && typeof window.productQuickView.open === "function") {
            window.productQuickView.open(productHandle);
        }
    } catch (_) {
        // swallow
    }
});

/**
 * Opens the Quick Add modal for the product represented by the wishlist (heart) button.
 * Works across product cards and PDP by reusing an existing quick-add-component if present,
 * or creating a temporary one with an explicit product URL.
 * @param {HTMLElement} btn
 */
// Note: The prior Quick Add modal trigger has been retired in favor of Product Quick View.

// Respond to variant updates rendered on cards to keep button state fresh when variant changes
document.addEventListener("variant:update", () => {
    // Delay to allow DOM morphing to complete
    setTimeout(syncAllButtons, 0);
});

document.addEventListener("DOMContentLoaded", () => {
    syncAllButtons();
    updateWishlistIconCounts(false);
    // Make header wishlist icon open drawer when present
    const headerWishlistButton = document.querySelector("wishlist-drawer-component > button");
    const headerWishlistIcon = document.querySelector("wishlist-icon");
    if (headerWishlistButton && headerWishlistIcon) {
        headerWishlistIcon.addEventListener("click", (e) => {
            e.preventDefault();
            headerWishlistButton.dispatchEvent(new Event("click", { bubbles: true }));
        });
    }
});

// Update header icon when store changes
document.addEventListener("wishlist:change", () => updateWishlistIconCounts());

// Wishlist page rendering
let __wishlistRenderVersion = 0;
/** @param {string} handle */
async function fetchProduct(handle) {
    try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) return null;
        return await res.json();
    } catch (_) {
        return null;
    }
}

const __shippingCache = new Map();
/**
 * Fetches a map of variantId -> shippingSpeed ('standard'|'fast'|'express') by parsing product HTML
 * for our variant picker inputs which include data-shipping-speed set in Liquid.
 * @param {string} handle
 * @returns {Promise<Map<string, string>>}
 */
async function fetchVariantShippingMap(handle) {
    if (__shippingCache.has(handle)) return __shippingCache.get(handle);
    const map = new Map();
    try {
        const res = await fetch(`/products/${handle}`);
        if (!res.ok) throw new Error("fetch product html failed");
        const htmlText = await res.text();
        const doc = new DOMParser().parseFromString(htmlText, "text/html");
        const inputs = doc.querySelectorAll('input[type="radio"][data-variant-id][data-shipping-speed]');
        inputs.forEach((inp) => {
            if (!(inp instanceof HTMLInputElement)) return;
            const vid = inp.getAttribute("data-variant-id");
            const speed = inp.getAttribute("data-shipping-speed") || "standard";
            if (vid) map.set(String(vid), speed);
        });
        // Backward compat: check old data-ship-48h attribute
        if (map.size === 0) {
            doc.querySelectorAll('input[type="radio"][data-variant-id][data-ship-48h]').forEach((inp) => {
                if (!(inp instanceof HTMLInputElement)) return;
                const vid = inp.getAttribute("data-variant-id");
                const fast = inp.getAttribute("data-ship-48h") === "true";
                if (vid) map.set(String(vid), fast ? "express" : "standard");
            });
        }
    } catch (_) {
        // leave map empty on failure
    }
    __shippingCache.set(handle, map);
    return map;
}

async function renderWishlistPage() {
    const containers = /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll("[data-wishlist-page]"));
    if (!containers.length) return;
    const version = ++__wishlistRenderVersion;
    const items = WishlistStore.get();
    dbg("render:start", { version, items: items.length, containers: containers.length });

    // If empty, update states immediately and exit; otherwise defer DOM changes until data is ready
    if (items.length === 0) {
        containers.forEach((container) => {
            const listEl = /** @type {HTMLElement | null} */ (container.querySelector("[data-wishlist-items]"));
            const emptyEl = /** @type {HTMLElement | null} */ (container.querySelector("[data-wishlist-empty]"));
            if (listEl) listEl.innerHTML = "";
            if (emptyEl) emptyEl.classList.remove("hidden");
        });
        return;
    }

    // Fetch products in parallel
    const products = await Promise.all(items.map((it) => fetchProduct(it.productHandle)));
    const shipMaps = await Promise.all(items.map((it) => fetchVariantShippingMap(it.productHandle)));
    if (version !== __wishlistRenderVersion) {
        dbg("render:aborted", { version });
        return;
    }

    containers.forEach((container) => {
        const listEl = /** @type {HTMLElement | null} */ (container.querySelector("[data-wishlist-items]"));
        const emptyEl = /** @type {HTMLElement | null} */ (container.querySelector("[data-wishlist-empty]"));
        if (!(listEl && emptyEl)) return;

        const frag = document.createDocumentFragment();
        items.forEach((item, idx) => {
            const product = products[idx];
            if (!product) return;
            const li = document.createElement("li");
            // Use a flex-based layout for wishlist rows
            li.className = "wishlist-item";

            const variants = Array.isArray(product.variants) ? product.variants : [];
            const selectedVariant = item.variantId
                ? variants.find((/** @type {any} */ v) => String(v.id) === String(item.variantId)) || variants[0]
                : variants[0];
            const isAvailable = !!(selectedVariant && selectedVariant.available);

            const img = product.images?.[0] || null;

            // Build variant options display (e.g., size) and price row
            let sizeHtml = "";
            if (selectedVariant && product.options && product.options.length > 0) {
                for (let i = 0; i < product.options.length; i++) {
                    const optionValue = selectedVariant[`option${i + 1}`];
                    if (optionValue && optionValue !== "Default Title") {
                        sizeHtml = `<div class="wishlist-item__variant">Selected size: <span>${optionValue}</span></div>`;
                        break;
                    }
                }
            }

            // Price (formatted with shop currency when available)
            const fmtCurrency = (cents) => {
                try {
                    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || "EUR";
                    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);
                } catch (_) {
                    return `$${(cents / 100).toFixed(2)}`;
                }
            };
            const price = selectedVariant?.price ? fmtCurrency(selectedVariant.price) : "";
            const shipMap = shipMaps[idx] || new Map();
            const shippingSpeed = selectedVariant ? shipMap.get(String(selectedVariant.id)) || "standard" : "standard";

            // Determine size and color values from options for Notify context
            let sizeVal = "";
            let colorVal = "";
            try {
                const optNames = Array.isArray(product.options) ? product.options : [];
                for (let i = 0; i < optNames.length; i++) {
                    const name = String(optNames[i] || "");
                    const val = selectedVariant ? selectedVariant[`option${i + 1}`] : "";
                    if (!val || val === "Default Title") continue;
                    const lower = name.toLowerCase();
                    if (!sizeVal && lower.includes("size")) sizeVal = val;
                    if (!colorVal && (lower.includes("color") || lower.includes("colour"))) colorVal = val;
                }
                // Fallbacks: if no explicit size/color identified, try first non-default option as size
                if (!sizeVal && selectedVariant) {
                    for (let i = 0; i < (product.options?.length || 0); i++) {
                        const val = selectedVariant[`option${i + 1}`];
                        if (val && val !== "Default Title") {
                            sizeVal = val;
                            break;
                        }
                    }
                }
            } catch (_) {}

            const actionsHtml = isAvailable
                ? `<button type="button" class="button button-secondary" data-action="add-to-cart" data-product-handle="${
                      item.productHandle
                  }" data-variant-id="${selectedVariant?.id ?? ""}">Move to cart</button>`
                : `<button type="button" class="button button-secondary button--soldout" data-action="notify" data-product-handle="${
                      item.productHandle
                  }" data-product-title="${(product.title || "").replace(/"/g, "&quot;")}" data-product-id="${
                      product.id ?? ""
                  }" data-variant-id="${selectedVariant?.id ?? ""}" data-size="${(sizeVal || "").replace(
                      /"/g,
                      "&quot;",
                  )}" data-color="${(colorVal || "").replace(/"/g, "&quot;")}">Notify me</button>`;

            li.innerHTML = `
                                <div class="cart-items__media" role="cell">
                                    ${
                                        img
                                            ? `<a href="${product.url}" class="cart-items__media-container" style="--ratio:1;">
                                                             <img src="${img}" alt="${product.title}" class="cart-items__media-image border-style" loading="lazy" />
                                                         </a>`
                                            : ""
                                    }
                                </div>
                                <div class="wishlist-item__content">
                                    <div class="wishlist-item__main cart-primary-typography" role="cell">
                                        <p><a href="${product.url}" class="cart-items-title">${product.title}</a></p>
                                    </div>
                                    <div class="wishlist-item__meta" role="cell">
                                        ${
                                            price
                                                ? `<div class="wishlist-item__price"><span class="label">Price:</span> <span>${price}</span></div>`
                                                : ""
                                        }
                                        ${sizeHtml || ""}
                                        <div class="wishlist-item__actions">${actionsHtml}</div>
                                    </div>
                                </div>
                                <button type="button" class="button button--tertiary cart-items__remove" data-action="remove" data-product-handle="${
                                    item.productHandle
                                }" data-variant-id="${item.variantId ?? ""}" aria-label="Remove from wishlist"></button>
                        `;

            // Inject trash icon into remove button to match cart
            const trashTmpl = document.getElementById("wishlist-trash-icon");
            const removeBtn = li.querySelector('[data-action="remove"]');
            if (trashTmpl instanceof HTMLTemplateElement && removeBtn) {
                const first = trashTmpl.content.firstElementChild;
                const clone = first ? first.cloneNode(true) : null;
                if (clone) removeBtn.appendChild(clone);
                const sr = document.createElement("span");
                sr.className = "visually-hidden";
                sr.textContent = "Remove";
                removeBtn.appendChild(sr);
            }

            frag.appendChild(li);
        });

        if (version !== __wishlistRenderVersion) return;
        // Atomically swap content to avoid flicker
        listEl.replaceChildren(frag);

        if (!listEl.dataset.wishlistBound) {
            listEl.dataset.wishlistBound = "1";
            listEl.addEventListener("click", async (e) => {
                const target = e.target instanceof Element ? e.target : null;
                if (!target) return;
                if (target.matches('[data-action="remove"]')) {
                    e.preventDefault();
                    WishlistStore.remove({
                        productHandle: target.getAttribute("data-product-handle") || "",
                        variantId: target.getAttribute("data-variant-id"),
                    });
                    updateWishlistIconCounts();
                    return;
                }
                if (target.matches('[data-action="notify"]')) {
                    e.preventDefault();
                    const variantId = target.getAttribute("data-variant-id") || "";
                    const productId = target.getAttribute("data-product-id") || "";
                    const productTitle = target.getAttribute("data-product-title") || "";
                    const size = target.getAttribute("data-size") || "";
                    const color = target.getAttribute("data-color") || "";
                    try {
                        // Pre-populate modal fields
                        const pid = document.getElementById("notify-product-id");
                        const ptitle = document.getElementById("notify-product-title");
                        const pname = document.querySelector(".notify-me-product-name");
                        const colorVal = document.getElementById("notify-color-value");
                        const colorDisp = document.getElementById("notify-color-display");
                        if (pid) pid.value = productId;
                        if (ptitle) ptitle.value = productTitle;
                        if (pname && productTitle) pname.textContent = productTitle;
                        if (color && colorVal && colorDisp) {
                            colorVal.value = color;
                            colorDisp.textContent = "Color: " + color;
                        }
                    } catch (_) {}
                    if (typeof window.openNotifyModal === "function") {
                        window.openNotifyModal(variantId, size);
                    }
                    return;
                }
                if (target.matches('[data-action="add-to-cart"]')) {
                    e.preventDefault();
                    const variantId = target.getAttribute("data-variant-id");
                    if (!variantId) return;
                    // Prevent double-submits while request is in flight
                    const btn = /** @type {HTMLButtonElement} */ (target);
                    btn.disabled = true;
                    btn.setAttribute("aria-disabled", "true");
                    try {
                        await fetch(Theme?.routes?.cart_add_url || "/cart/add.js", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
                        });
                        WishlistStore.remove({
                            productHandle: target.getAttribute("data-product-handle") || "",
                            variantId,
                        });
                        updateWishlistIconCounts();
                        document.dispatchEvent(
                            new CustomEvent("cart:update", { detail: { data: { itemCount: 1, source: "wishlist" } } }),
                        );
                    } catch (_) {
                        // Re-enable if the request failed
                        btn.disabled = false;
                        btn.setAttribute("aria-disabled", "false");
                    }
                }
            });
        }
    });

    dbg("render:done", { version });
    // Final pass: ensure empty message visibility matches the current DOM state
    try {
        const containersNow = /** @type {NodeListOf<HTMLElement>} */ (
            document.querySelectorAll("[data-wishlist-page]")
        );
        containersNow.forEach((container) => {
            const listEl = /** @type {HTMLElement | null} */ (container.querySelector("[data-wishlist-items]"));
            const emptyEl = /** @type {HTMLElement | null} */ (container.querySelector("[data-wishlist-empty]"));
            if (!(listEl && emptyEl)) return;
            const hasItems = listEl.children.length > 0;
            emptyEl.classList.toggle("hidden", hasItems);
        });
    } catch (_) {}
    // Re-assert header bubble visibility/state in case any DOM work during render affected it
    try {
        updateWishlistIconCounts(false);
        reassertWishlistBubbleWithRetries();
    } catch (_) {}
}

document.addEventListener("DOMContentLoaded", renderWishlistPage);
// Debounce renders to avoid overlapping DOM work causing transient empty states
let __wishlistRenderScheduled = false;
function scheduleWishlistRender() {
    if (__wishlistRenderScheduled) return;
    __wishlistRenderScheduled = true;
    requestAnimationFrame(() => {
        __wishlistRenderScheduled = false;
        renderWishlistPage();
    });
}

document.addEventListener("wishlist:open", scheduleWishlistRender);
document.addEventListener("wishlist:change", scheduleWishlistRender);
